import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, MessageSquare, Users, CheckCircle2 } from "lucide-react";

function Pairs({ items }) {
  if (!items?.length) return <p className="text-sm text-muted-foreground italic">Inget ifyllt ännu.</p>;
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {items.map((it, i) => (
        <div key={i}>
          <dt className="text-xs font-medium text-muted-foreground">{it.label}</dt>
          <dd className="text-sm text-foreground mt-0.5 whitespace-pre-line">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Entries({ items }) {
  if (!items?.length) return <p className="text-sm text-muted-foreground italic">Inget tillagt ännu.</p>;
  return (
    <div className="space-y-4">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-border p-4">
          <div className="font-semibold text-sm mb-2">{it.heading}</div>
          <Pairs items={it.pairs} />
        </div>
      ))}
    </div>
  );
}

function Interview({ items }) {
  if (!items?.length) return <p className="text-sm text-muted-foreground italic">Intervjun är inte påbörjad ännu.</p>;
  return (
    <div className="space-y-4">
      {items.map((sec, i) => (
        <div key={i} className="rounded-xl border border-border p-4">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> {sec.title}</div>
          {sec.summary && (
            <p className="text-sm text-foreground bg-primary/5 border border-primary/15 rounded-lg px-3 py-2 mb-3 italic">
              Sammanfattning: {sec.summary}
            </p>
          )}
          <div className="space-y-2.5">
            {sec.qa.map((qa, j) => (
              <div key={j}>
                <div className="text-xs font-medium text-muted-foreground">{qa.prompt}</div>
                <div className="text-sm text-foreground mt-0.5 whitespace-pre-line">{qa.answer}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function RefGroups({ groups }) {
  if (!groups?.length) return <p className="text-sm text-muted-foreground italic">Inga referenssvar per dimension ännu.</p>;
  return (
    <div className="space-y-4">
      {groups.map((g, i) => (
        <div key={i} className="rounded-xl border border-border p-4">
          <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> {g.title}</div>
          <div className="space-y-2">
            {g.answers.map((a, j) => (
              <div key={j} className="text-sm">
                <span className="text-foreground">{a.cannot_assess ? <em className="text-muted-foreground">Kan ej bedöma</em> : `\u201c${a.text}\u201d`}</span>
                <span className="text-muted-foreground"> — {a.name}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SectionBody({ s }) {
  if (s.type === "pairs") return <Pairs items={s.items} />;
  if (s.type === "entries") return <Entries items={s.items} />;
  if (s.type === "interview") return <Interview items={s.items} />;
  if (s.type === "refgroups") return <RefGroups groups={s.groups} />;
  if (s.type === "text") return s.text
    ? <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">{s.text}</p>
    : <p className="text-sm text-muted-foreground italic">Inget skapat ännu.</p>;
  return null;
}

export function UnderlagSections({ sections, reviewed = [], onToggleReview = null, onEdit = null }) {
  const canReview = typeof onToggleReview === "function";
  return (
    <div className="space-y-5">
      {sections.map((s) => {
        const isReviewed = reviewed.includes(s.id);
        return (
          <Card
            key={s.id}
            className={`rounded-2xl p-6 transition-colors ${isReviewed ? "border-primary/40 bg-primary/[0.03]" : "border-border"}`}
            data-testid={`underlag-section-${s.id}`}
          >
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <h2 className="font-head font-semibold text-lg">{s.title}</h2>
                {isReviewed && <CheckCircle2 className="h-4 w-4 text-primary" data-testid={`underlag-reviewed-icon-${s.id}`} />}
              </div>
              <div className="flex items-center gap-3">
                {canReview && (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                    <Checkbox checked={isReviewed} onCheckedChange={(v) => onToggleReview(s.id, !!v)} data-testid={`underlag-review-${s.id}`} />
                    Granskad
                  </label>
                )}
                {onEdit && s.edit && (
                  <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-foreground" onClick={() => onEdit(s.edit)} data-testid={`underlag-edit-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Redigera
                  </Button>
                )}
              </div>
            </div>
            <SectionBody s={s} />
          </Card>
        );
      })}
    </div>
  );
}
